import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const inert = (e) => e.preventDefault();

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* ================= Block 1 — Hero ================= */

const CertificationHero = () => (
  <section data-testid="certification-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
        <div className="mx-auto max-w-[62rem] text-center lg:mx-0 lg:max-w-none lg:text-left">
          <div className="flex items-center justify-center gap-3 lg:justify-start" aria-hidden="true">
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 lg:hidden" />
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
              WFII Certification
            </span>
            <span className="h-[3px] w-3 bg-[hsl(var(--accent))] lg:hidden" />
            <span className="h-px w-10 bg-[hsl(var(--primary))]/25 lg:hidden" />
          </div>
          <h1 className="font-serif-editorial mx-auto mt-8 text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:mx-0 lg:text-[3.4rem]">
            Recognized Learning for Modern Finance Professionals
          </h1>
          <p className="mx-auto mt-8 max-w-[74ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0 lg:max-w-[52ch]">
            WFII certification programs build practical knowledge in digital
            money, stablecoins, payments, accounting, audit and assurance,
            treasury, and compliance.
          </p>
          <p className="mx-auto mt-4 max-w-[74ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0 lg:max-w-[52ch]">
            Each certification is linked to a WFII program and designed for
            real-world financial workflows.
          </p>
        </div>
        <div
          className="mx-auto w-full max-w-md overflow-hidden rounded-2xl shadow-[0_40px_80px_-40px_hsl(218_51%_15%/0.4)] ring-1 ring-[hsl(var(--primary))]/10 lg:mx-0 lg:ml-auto lg:w-[93.5%] lg:max-w-none"
          data-testid="certification-hero-image"
        >
          <img
            src="/certificate-sample.png"
            alt="Sample WFII certificate of completion"
            className="wfii-photo aspect-[16/10] w-full object-cover sm:aspect-[21/9] lg:aspect-[3/2]"
          />
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — How certification works ================= */

const CERT_INCLUDES = [
  "Online learning modules",
  "Business-focused examples",
  "Practical resources and checklists",
  "Final knowledge assessment",
  "WFII certificate of completion",
  "Digital certificate or badge",
];

const HowItWorks = () => (
  <section data-testid="certification-how-section">
    <Container className="">
      {/* Centered heading + supporting text */}
      <div className="mx-auto max-w-3xl text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <h2 className="mt-8 text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          How WFII Certification Works
        </h2>
        <p className="mx-auto mt-7 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg">
          WFII certifications are awarded after completion of eligible
          programs, including learning modules, practical materials, and a
          final knowledge assessment.
        </p>
      </div>

      {/* Full-width horizontal feature list */}
      <div className="mt-14">
        <span className="block text-center text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
          Each certification includes
        </span>
        <div
          className="mt-9 grid sm:grid-cols-2 lg:grid-cols-3"
          data-testid="certification-includes-list"
        >
          {CERT_INCLUDES.map((item) => (
            <div
              key={item}
              className="flex items-center gap-3.5 px-2 py-4 sm:px-6 lg:py-[18px]"
              data-testid={`certification-includes-item-${item.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Check
                className="h-[18px] w-[18px] shrink-0 text-[hsl(var(--wfii-teal))]"
                strokeWidth={2}
                aria-hidden="true"
              />
              <span className="text-sm leading-relaxed text-foreground/85 sm:text-base">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 3 — Available certification programs ================= */

const CERTIFICATES = [
  {
    id: "stablecoins",
    title: "Digital Money and Stablecoins Certificate",
    status: "available",
    body: "This certificate recognizes fundamental understanding of digital money, stablecoins, wallets, payment rails, and their role in business finance.",
    demonstrates:
      "Clear understanding of core digital finance concepts in a business context.",
    credential: "WFII Certificate of Completion",
    href: "/programs/digital-money-and-stablecoins",
  },
  {
    id: "payments",
    title: "Business Payments and Treasury Certificate",
    status: "available",
    body: "This certificate recognizes understanding of business payments, payouts, settlement, liquidity, treasury workflows, and payment records in the context of modern financial infrastructure.",
    demonstrates:
      "Clear understanding of how digital finance can connect to real payment and treasury operations.",
    credential: "WFII Certificate of Completion",
    href: "/programs/business-payments-and-treasury",
  },
  {
    id: "accounting",
    title: "Digital Asset Accounting Certificate",
    status: "available",
    body: "This certificate recognizes understanding of digital asset records, transaction history, documentation, reconciliation, and reporting considerations.",
    demonstrates:
      "Clear understanding of how to approach digital asset activity through an accounting and records-focused lens.",
    credential: "WFII Certificate of Completion",
    href: "/programs/digital-asset-accounting",
  },
  {
    id: "compliance",
    title: "Compliance for Digital Finance Operations Certificate",
    status: "coming-soon",
    body: "This certificate will recognize understanding of the controls, documentation, governance, risk, and operational questions connected to digital finance workflows.",
    demonstrates:
      "Clear understanding of key compliance and operational readiness considerations before adoption.",
    credential: "WFII Certificate of Completion",
    href: "/programs/compliance-for-digital-finance-operations",
  },
  {
    id: "audit-assurance",
    title: "Digital Asset Audit & Assurance Certificate",
    status: "coming-soon",
    body: "This certificate will recognize understanding of digital asset audit evidence, transaction verification, balances, valuation, documentation, internal controls, and reporting considerations.",
    demonstrates:
      "Practical understanding of how digital asset activity can be evaluated in an audit and assurance context.",
    credential: "WFII Certificate of Completion",
    href: "/programs/digital-asset-audit-and-assurance",
  },
];

const CertificateCard = ({ c }) => (
  <article
    className="flex h-full flex-col rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-9 lg:pt-[30px] lg:pb-10 lg:px-[30px] lg:shadow-none lg:hover:shadow-none"
    data-testid={`certification-card-${c.id}`}
  >
    <h3 className="text-xl font-semibold leading-snug tracking-[-0.01em] text-foreground sm:text-2xl">
      {c.title}
    </h3>
    <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
      {c.body}
    </p>
    <div className="mt-6 space-y-4 border-t border-[hsl(var(--primary))]/10 pt-6">
      <div>
        <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Demonstrates
        </span>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">
          {c.demonstrates}
        </p>
      </div>
      <div>
        <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Credential
        </span>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">{c.credential}</p>
      </div>
    </div>
    <div className="mt-8 flex flex-1 items-end">
      {c.href ? (
        <Button
          asChild
          variant="outline"
          className="h-11 w-full border-[hsl(var(--primary))]/25 bg-transparent px-6 text-sm font-medium transition-colors hover:bg-[hsl(var(--accent))] hover:text-white hover:border-transparent active:scale-[0.98]"
          data-testid={`certification-card-${c.id}-view-button`}
        >
          <Link to={c.href}>View Program</Link>
        </Button>
      ) : (
        <Button
          variant="outline"
          className="h-11 w-full border-[hsl(var(--primary))]/25 bg-transparent px-6 text-sm font-medium transition-colors hover:bg-[hsl(var(--accent))] hover:text-white hover:border-transparent active:scale-[0.98]"
          onClick={inert}
          data-testid={`certification-card-${c.id}-get-updates-button`}
        >
          Get Updates
        </Button>
      )}
    </div>
  </article>
);

const CertificatePrograms = () => {
  const total = CERTIFICATES.length;
  const extended = [...CERTIFICATES, ...CERTIFICATES, ...CERTIFICATES];
  const viewportRef = useRef(null);
  const [startIndex, setStartIndex] = useState(total);
  const [step, setStep] = useState(0);
  const [transitionOn, setTransitionOn] = useState(true);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => {
      const cs = getComputedStyle(el);
      const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const contentWidth = el.clientWidth - paddingX;
      setStep((contentWidth - 64) / 3 + 32);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!transitionOn) {
      const raf = requestAnimationFrame(() => setTransitionOn(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [transitionOn]);

  const goTo = (dir) => setStartIndex((s) => s + dir);

  const handleTransitionEnd = () => {
    if (startIndex >= total * 2) {
      setTransitionOn(false);
      setStartIndex((s) => s - total);
    } else if (startIndex < total) {
      setTransitionOn(false);
      setStartIndex((s) => s + total);
    }
  };

  return (
  <section data-testid="certification-programs-section">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center lg:max-w-[62rem]">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          Certification Programs
        </h2>
        <p className="mt-7 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Start with any program based on your role.
        </p>
        <p className="mx-auto mt-3 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:max-w-none">
          Some professionals begin with Digital Money and Stablecoins for a
          foundation,{" "}
          <br className="hidden lg:block" />
          while others start with accounting, payments, treasury, or compliance
          depending on their work.
        </p>
      </div>

      {/* Mobile / tablet — stacked cards */}
      <div className="mt-14 grid gap-6 lg:hidden" data-testid="certification-programs-mobile-list">
        {CERTIFICATES.map((c) => (
          <CertificateCard key={c.id} c={c} />
        ))}
      </div>

      {/* Desktop — 3-up infinite slider across 5 cards */}
      <div className="relative mt-14 hidden lg:block">
        <button
          type="button"
          onClick={() => goTo(-1)}
          aria-label="Previous certificates"
          className="absolute left-0 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(var(--primary))]/15 bg-card/95 p-3 text-foreground/70 shadow-[0_12px_28px_-12px_hsl(218_51%_15%/0.35)] backdrop-blur-sm transition-all hover:text-foreground hover:shadow-[0_16px_32px_-12px_hsl(218_51%_15%/0.45)]"
          data-testid="certification-programs-slider-prev-button"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={1.75} />
        </button>
        <div
          ref={viewportRef}
          className="overflow-hidden px-4 pt-4 pb-3"
          data-testid="certification-programs-slider-track"
        >
          <div
            className="flex gap-8"
            style={{
              transform: `translateX(-${startIndex * step}px)`,
              transition: transitionOn ? "transform 0.5s ease-in-out" : "none",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {extended.map((c, i) => (
              <div key={`${c.id}-${i}`} className="w-[calc((100%-64px)/3)] shrink-0">
                <CertificateCard c={c} />
              </div>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => goTo(1)}
          aria-label="Next certificates"
          className="absolute right-0 top-1/2 z-10 flex translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(var(--primary))]/15 bg-card/95 p-3 text-foreground/70 shadow-[0_12px_28px_-12px_hsl(218_51%_15%/0.35)] backdrop-blur-sm transition-all hover:text-foreground hover:shadow-[0_16px_32px_-12px_hsl(218_51%_15%/0.45)]"
          data-testid="certification-programs-slider-next-button"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </div>
    </Container>
  </section>
  );
};

/* ================= Block 4 — What the credential represents ================= */

const CredentialMeaning = () => (
  <section data-testid="certification-credential-section">
    <Container className="">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
          What the Credential Shows
        </h2>
        <p className="mx-auto mt-7 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg">
          WFII certificates confirm that participants have completed the
          program, passed the final assessment, and can apply key concepts to
          real financial workflows.
        </p>
        <p className="mx-auto mt-4 max-w-[64ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:max-w-none lg:whitespace-nowrap">
          Designed for professionals and organizations building practical
          knowledge in digital finance.
        </p>
        <div className="mx-auto mt-9 h-px w-16 bg-[hsl(var(--primary))]/15" aria-hidden="true" />
        <div className="mx-auto mt-9 grid max-w-[62ch] gap-4 text-left lg:max-w-[68rem] lg:grid-cols-2">
          <div
            className="rounded-lg bg-[hsl(174_45%_40%/0.06)] p-5 ring-1 ring-[hsl(var(--accent))]/15 lg:text-center"
            data-testid="certification-credential-disclaimer"
          >
            <p className="text-sm leading-relaxed text-foreground/80">
              WFII certificates are educational credentials. They do not replace
              legal, accounting, tax, audit, regulatory, or professional
              licensing qualifications.
            </p>
          </div>
          <div
            className="rounded-lg bg-[hsl(174_45%_40%/0.06)] p-5 ring-1 ring-[hsl(var(--accent))]/15 lg:text-center"
            data-testid="certification-credential-disclaimer-audit"
          >
            <p className="text-sm leading-relaxed text-foreground/80">
              WFII certificates do not confer professional auditor status or
              authorization to perform regulated audit or assurance
              engagements.
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 5 — Final CTA ================= */

const CertificationFinalCta = () => (
  <section data-testid="certification-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Start With the Certificate{" "}
            <br className="hidden lg:block" />
            That Fits Your Work
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              Begin with the WFII program most relevant to your role, from
              digital money{" "}
              <br className="hidden lg:block" />
              and stablecoins to accounting, payments, treasury, or compliance.
            </p>
            <p className="mt-4 text-base leading-relaxed text-primary-foreground/75 sm:text-lg lg:-mx-12 lg:whitespace-nowrap">
              WFII members receive preferred pricing on eligible programs and
              certification options.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="certification-final-cta-explore-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
              <Link
                to="/enrollment-request" state={{ type: "membership" }}
                className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-primary-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-md"
                data-testid="certification-final-cta-become-member-link"
              >
                Become a Member
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={1.75}
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function CertificationPathwaysPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <CertificationHero />
          <HowItWorks />
          <CertificatePrograms />
          <CredentialMeaning />
          <CertificationFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
